import { t } from 'plottr_locales'

/**
 * @typedef {import('../types/system').KnownFilesEntry} KnownFile
 * @typedef {import('../types/system').OfflineFilesEntry} OfflineFile
 * @typedef {import('../types/system').CreatedOfflineFileEntry} CreatedOfflineFileEntry
 * @typedef {import('../index').FileURL} FileURL
 * @typedef {import('../index').File} File
 */

/**
 * @param {String} fileURL
 * @return {boolean}
 */
export const urlPointsToPlottrCloud = (fileURL) => !!(fileURL && fileURL.startsWith('plottr://'))

/**
 * @param {String} fileURL
 * @return {boolean}
 */
export const urlPointsToOfflineInbox = (fileURL) => !!(fileURL && fileURL.startsWith('offline://'))

/**
 * @param {String} fileId
 * @return {FileURL}
 */
export const fileIdToPlottrCloudFileURL = (fileId) => {
  return `plottr://${fileId}`
}

/**
 * @param {OfflineFile | KnownFile | CreatedOfflineFileEntry} file
 * @return {boolean}
 */
export const isPlottrCloudFile = (file) => file && urlPointsToPlottrCloud(file.fileURL)

/**
 * @param {KnownFile | OfflineFile | CreatedOfflineFileEntry} file
 * @return {boolean}
 */
export const isOfflineInboxFile = (file) => {
  return file && urlPointsToOfflineInbox(file.fileURL)
}

/**
 * @param {String} url
 * @return {boolean}
 */
export const isDeviceFileURL = (url) => {
  return !!(url && url.startsWith('device://'))
}

/**
 * @param {String} url
 * @return {boolean}
 */
export const isCreatedOfflineFileURL = (url) => {
  return !!(url && url.startsWith('offline://'))
}

/**
 * @param {String} url
 * @return {boolean}
 */
export const isStoragePath = (url) => {
  return !!(url && url.startsWith('storage://'))
}

/**
 * @param {String} fileURL
 * @return {String | Null}
 */
export const fileIdFromPlottrProFile = (fileURL) => {
  if (fileURL && fileURL.startsWith('plottr://')) {
    return fileURL.substring(9)
  }
  return null
}

/**
 * @param {String} s
 * @return {boolean}
 */
export const isProtocolString = (s) => {
  return !!s?.match?.(/^[a-z]+:\/\//)
}

/**
 * @param {String} s
 * @return {String | undefined}
 */
export const protocolString = (s) => {
  return s.match(/^[a-z]+:\/\//)?.[0]
}

/**
 * @param {String} fileURL
 * @return {String | Null}
 */
export const withoutProtocol = (fileURL) => {
  if (!fileURL || typeof fileURL.match !== 'function') {
    return null
  }

  const protocolMatch = fileURL.match(/^[a-z]+:\/\//)
  if (protocolMatch && typeof fileURL.substring === 'function') {
    return fileURL.substring(protocolMatch[0].length)
  }

  return fileURL
}

/**
 * @param {{ nanoseconds: number, seconds: number }} nanosAndSecondsObject
 * @return {Date | Null}
 */
const convertFromNanosAndSeconds = (nanosAndSecondsObject) => {
  if (
    !nanosAndSecondsObject ||
    !nanosAndSecondsObject.nanoseconds ||
    !nanosAndSecondsObject.seconds
  ) {
    return null
  }
  return new Date(
    nanosAndSecondsObject.seconds * 1000 + nanosAndSecondsObject.nanoseconds / 1000000
  )
}

export const VERY_OLD_DATE = new Date(0)

/**
 * @param {KnownFile | OfflineFile | CreatedOfflineFileEntry} fileObj
 * @return {Date}
 */
export function getDateValue(fileObj) {
  if (!fileObj.lastOpened) {
    return VERY_OLD_DATE
  }

  // At some point, we stored a timestamp in this field, then we went
  // to a `seconds`, and `nanoseconds` object.  Now, we're back to a
  // timestamp because it plays better with mobile's use of Sagas.
  const lastOpenedIsString = typeof fileObj.lastOpened === 'string'
  const lastOpenedIsNumber = typeof fileObj.lastOpened === 'number'

  if (
    !lastOpenedIsString &&
    fileObj.lastOpened &&
    typeof fileObj.lastOpened === 'object' &&
    'nanoseconds' in fileObj.lastOpened &&
    'seconds' in fileObj.lastOpened
  ) {
    return convertFromNanosAndSeconds(fileObj.lastOpened) || new Date()
  }

  if (lastOpenedIsNumber) {
    return new Date(fileObj.lastOpened)
  }

  if (lastOpenedIsString) {
    return new Date(fileObj.lastOpened)
  }

  try {
    if ('version' in fileObj && typeof fileObj.version === 'string') {
      const splits = fileObj.version.replace(/-.*$/, '').split('.')
      return new Date(parseInt(splits[0]), parseInt(splits[1]) - 1, parseInt(splits[2]))
    }
  } catch (_error) {
    // do nothing
  }

  return new Date()
}

/**
 * @param {String} filePath
 * @return {String}
 */
export const filePathToOfflineProFileURL = (filePath) => {
  return `offline://${filePath}`
}

/**
 * @param {String} filePath
 * @return {String | Null}
 */
export const filePathToFileURL = (filePath) => {
  if (!filePath) {
    return null
  }

  return `device://${filePath}`
}

/**
 * @param {String} filePath
 * @return {String | Null}
 */
export const ensureEndsInPltr = (filePath) => {
  if (!filePath) return null

  if (!filePath.endsWith('.pltr')) {
    return `${filePath}.pltr`
  }
  return filePath
}

/**
 * @param {String} path1
 * @param {String} path2
 */
export const neitherPathContainsTheOther = (path1, path2) => {
  if (
    (typeof path1 === 'string' && typeof path2 !== 'string') ||
    (typeof path1 === 'string' && typeof path2 !== 'string')
  ) {
    return true
  } else {
    const segments1 = path1.split(/\\|\//)
    const segments2 = path2.split(/\\|\//)
    const longer = segments1.length > segments2.length ? segments1 : segments2
    const shorter = segments1.length <= segments2.length ? segments1 : segments2
    for (let i = 0; i < shorter.length; ++i) {
      if (longer[i] !== shorter[i]) {
        return true
      }
    }
    return false
  }
}

/**
 * @param {String} name
 * @return {String}
 */
export const genericBackupNameForToday = (name) => {
  const date = new Date()
  const month = date.getMonth() + 1
  const day = date.getDate()
  const year = date.getUTCFullYear()
  const backupDate = `${t('Resumed at')}_${month}-${day}-${year}`
  const backupText = t('Backup')
  return `${name} [${backupText} ${backupDate}].pltr`
}
